import { Injectable } from '@angular/core';

interface StoredValue<T> {
  data: T;
  timestamp: number;
}
@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  private defaultExpiryMs = 1000 * 60 * 60 * 1; // 1 hour

  /** Save a value with an optional custom expiry (in milliseconds). */
  set<T>(key: string, value: T, expiryMs: number = this.defaultExpiryMs): void {
    const item: StoredValue<T> = {
      data: value,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify({ ...item, expiryMs }));
  }

  /** Get a value; returns null if expired or not found. */
  get<T>(key: string): T | null {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as StoredValue<T> & { expiryMs?: number };
      const expiry = parsed.expiryMs ?? this.defaultExpiryMs;
      const isExpired = Date.now() - parsed.timestamp > expiry;
      if (isExpired) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed.data;
    } catch {
      // if parsing fails (e.g., corrupted entry), clear it
      localStorage.removeItem(key);
      return null;
    }
  }

  /** Remove a stored item immediately. */
  remove(key: string): void {
    localStorage.removeItem(key);
  }

  /** Check if a key exists and is not expired. */
  has(key: string): boolean {
    return this.get(key) !== null;
  }
}
